import {
  FormEvent, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../app/hooks';
import {
  PayerRequestState,
  setPayerRequest,
} from '../features/booking/bookingSlice';
import {
  isValidPayerEmail,
  PayerRequestError,
  requestPayerPayment,
  sameEmail,
} from '../util/payerPayment';
import Button from './Button';
import TextInput from './TextInput';

interface PayerPaymentNoteProps {
  /** Where the pay link went (or why it did not). */
  request: PayerRequestState;
  /** The booking's pay token: the only key the change is sent with. */
  token: string | null;
  /** The booker's own email, which the payer's may not be. */
  bookerEmail?: string;
  /** "Pay now" for the booker, when the link could not be sent. */
  onPayNow?: () => void;
  /** The prepay discount its label names ("Pay now and save 3%"). */
  prepayDiscountPercent?: number;
}

/**
 * The confirmation page's payment line for "Send the bill to your partner"
 * (Aperio preset; rendered by AperioConfirmation): "Sent! We emailed Pat the
 * link to pay." with a small "Wrong email? Change it" (three changes per
 * booking, the server counts), or, when the email could not be sent, a
 * retry and the booker's own "Pay now". Styled by `apr-payer-note*` in
 * public/aperio/theme.css.
 */
export default function PayerPaymentNote({
  request,
  token,
  bookerEmail,
  onPayNow,
  prepayDiscountPercent = 3,
}: PayerPaymentNoteProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  const [sending, setSending] = useState(false);
  const [serverError, setServerError] = useState<PayerRequestError | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const changeRef = useRef<HTMLButtonElement | null>(null);
  // Focus follows the disclosure: into the box when it opens, back to
  // "Change it" when it closes.
  const wasEditing = useRef(false);
  useEffect(() => {
    if (editing) inputRef.current?.focus();
    else if (wasEditing.current) changeRef.current?.focus();
    wasEditing.current = editing;
  }, [editing]);

  let fieldError: string | undefined;
  if (touched) {
    if (!email.trim()) fieldError = t('errors.payer.emailRequired');
    else if (!isValidPayerEmail(email)) fieldError = t('errors.email');
    else if (sameEmail(email, bookerEmail)) fieldError = t('errors.payer.self');
  }
  if (!fieldError && serverError) {
    fieldError = serverError === 'SELF'
      ? t('errors.payer.self')
      : t('confirm.payer.errorGeneric');
  }

  const send = async (to: string) => {
    if (!token || sending) return;
    setSending(true);
    setServerError(null);
    const result = await requestPayerPayment(token, to, request.name);
    setSending(false);
    if (result.ok) {
      dispatch(setPayerRequest({
        status: 'SENT',
        email: to.trim(),
        name: result.name ?? request.name,
        changesLeft: result.changesLeft,
      }));
      setEditing(false);
      setEmail('');
      setTouched(false);
      return;
    }
    if (result.error === 'LIMIT') {
      dispatch(setPayerRequest({ ...request, changesLeft: 0 }));
      setEditing(false);
      return;
    }
    if (request.status === 'FAILED' && !editing) {
      // The retry of a first send: stays FAILED, with the same message.
      return;
    }
    setServerError(result.error);
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (
      !isValidPayerEmail(email)
      || sameEmail(email, bookerEmail)
    ) return;
    send(email);
  };

  if (request.status === 'FAILED') {
    return (
      <div className="apr-payer-note">
        <p role="status">{t('confirm.payer.failed')}</p>
        <div className="apr-payer-note__actions">
          {token && (
            <Button
              variant="secondary"
              onClick={() => send(request.email)}
              loading={sending}
            >
              {t('confirm.payer.retry')}
            </Button>
          )}
          {onPayNow && (
            <Button onClick={onPayNow}>
              {t('prepay.title', { percent: prepayDiscountPercent })}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="apr-payer-note">
      <p role="status">
        <strong>
          {request.name
            ? t('confirm.payer.sentName', { name: request.name })
            : t('confirm.payer.sentThem')}
        </strong>
        {' '}
        {t('confirm.payer.body')}
      </p>
      {!editing && request.changesLeft > 0 && token && (
        <button
          type="button"
          className="apr-payer-note__change"
          ref={changeRef}
          onClick={() => setEditing(true)}
        >
          {t('confirm.payer.change')}
        </button>
      )}
      {!editing && request.changesLeft <= 0 && (
        <p className="apr-payer-note__fine">{t('confirm.payer.noChangesLeft')}</p>
      )}
      {editing && (
        <form
          id="apr-payer-change"
          className="apr-payer-note__form"
          onSubmit={onSubmit}
          noValidate
        >
          <TextInput
            ref={inputRef}
            id="payer-change-email"
            type="email"
            title={t('payment.payer.email')}
            value={email}
            autoComplete="off"
            required
            errorMessage={fieldError}
            hint={t('confirm.payer.changesLeft', { count: request.changesLeft })}
            onChange={(event: FormEvent<HTMLInputElement>) => {
              setEmail(event.currentTarget.value);
              setServerError(null);
            }}
            onBlur={() => setTouched(true)}
          />
          <div className="apr-payer-note__actions">
            <Button type="submit" loading={sending}>
              {t('confirm.payer.send')}
            </Button>
            <Button
              variant="tertiary"
              onClick={() => {
                setEditing(false);
                setEmail('');
                setTouched(false);
                setServerError(null);
              }}
            >
              {t('confirm.payer.cancel')}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
